import React, { useState, useEffect } from "react";
import {
  Container,
  Heading,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Button,
  useToast,
  Checkbox,
  Text,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";

import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { useAuth } from "../../contexts/AuthContext";

// Function to generate a random string for the meeting link
const generateRandomMeetingString = () => {
  // Expanded list of 100+ positive, inspirational words
  const inspirationalWords = [
    // Personal Growth & Mindset
    "success", "growth", "inspire", "achieve", "prosper", "thrive", "empower", 
    "motivate", "harmony", "strength", "victory", "progress", "momentum", "excel", 
    "elevate", "optimize", "transform", "focus", "purpose", "journey", "wisdom", 
    "vision", "passion", "create", "innovate", "advance", "pioneer", "discover",
    
    // Positive Emotions & States
    "joy", "peace", "gratitude", "bliss", "serenity", "abundance", "wonder", 
    "harmony", "balance", "clarity", "vibrant", "radiant", "energize", "glow",
    "flourish", "bloom", "soar", "shine", "brilliant", "luminous", "incredible",
    
    // Action & Movement
    "leap", "rise", "climb", "build", "forge", "craft", "construct", "launch", 
    "accelerate", "momentum", "forward", "upward", "onward", "beyond", "transcend",
    "summit", "pinnacle", "zenith", "apex", "breakthrough", "milestone",
    
    // Achievement & Success
    "triumph", "mastery", "excel", "accomplish", "achieve", "conquer", "overcome", 
    "surpass", "exceed", "dominate", "champion", "victory", "win", "succeed",
    "results", "reward", "merit", "prestige", "acclaim", "recognition", "distinction",
    
    // Community & Connection
    "alliance", "unity", "together", "synergy", "collaborate", "partnership", 
    "community", "network", "tribe", "connection", "bond", "link", "unite",
    
    // Resilience & Strength
    "resilient", "endure", "persist", "tenacity", "fortitude", "courage", "brave", 
    "fearless", "bold", "daring", "resolute", "determined", "steadfast", "unwavering",
    "grit", "robust", "mighty", "powerful", "invincible", "unstoppable", "limitless"
  ];
  
  // Function to get a random word not already selected
  const getUniqueRandomWord = (selectedWords: string[]) => {
    let word;
    do {
      word = inspirationalWords[Math.floor(Math.random() * inspirationalWords.length)];
    } while (selectedWords.includes(word));
    return word;
  };
  
  // Pick 3 unique random words from the list
  const word1 = getUniqueRandomWord([]);
  const word2 = getUniqueRandomWord([word1]);
  const word3 = getUniqueRandomWord([word1, word2]);
  
  // Add a random number for uniqueness (1000-9999)
  const randomNum = Math.floor(Math.random() * 9000) + 1000;
  
  // Return the formatted meeting link
  return `https://m.lifeessentials.club/${word1}-${word2}-${word3}-${randomNum}`;
};

interface SessionTypeFormData {
  name: string;
  description?: string;
  format: "INDIVIDUAL" | "GROUP";
  duration: number;
  price: number;
  color?: string;
  availableOnCalendar: boolean;
  clientId: string;
  meetingLink?: string;
}

const CREATE_SESSION_TYPE = gql`
  mutation CreateSessionType($input: SessionTypeInput!) {
    createSessionType(input: $input) {
      id
      name
      description
      format
      duration
      price
      color
      availableOnCalendar
      clientId
      meetingLink
    }
  }
`;

export const CreateSessionType = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  
  // Generate a default meeting link URL with random string when component mounts
  const [defaultMeetingLink] = useState(generateRandomMeetingString());
  
  const [formData, setFormData] = useState<SessionTypeFormData>({
    name: "",
    description: "",
    format: "INDIVIDUAL",
    duration: 60,
    price: 0,
    color: "#4A7C59",
    availableOnCalendar: true,
    clientId: user?.id || "",
    meetingLink: defaultMeetingLink,
  });

  const [createSessionType, { loading }] = useMutation(CREATE_SESSION_TYPE);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.id) {
      toast({
        title: "Authentication error",
        description: "You must be logged in to create a session type",
        status: "error",
        duration: 5000,
      });
      return;
    }
    
    // Log the form data before submission
    console.log("Submitting session type:", {
      ...formData,
      duration: Number(formData.duration),
      price: Number(formData.price),
      clientId: user.id,
      meetingLink: formData.meetingLink
    });

    try {
      await createSessionType({
        variables: {
          input: {
            ...formData,
            duration: Number(formData.duration),
            price: Number(formData.price),
            clientId: user.id,
            meetingLink: formData.meetingLink
          }
        },
        refetchQueries: ["GetSessionTypes"],
      });
      
      toast({
        title: "Session type created",
        status: "success",
        duration: 3000,
      });
      navigate("/sessions/types");
    } catch (error) {
      console.error("Error creating session type:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An error occurred",
        status: "error",
        duration: 5000,
      });
    }
  };

  if (!user?.id) {
    return <Container maxW="container.md" py={8}>
      <Text>Please log in to create a session type.</Text>
    </Container>;
  }

  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.md" py={8}>
        <VStack spacing={8} align="stretch">
          <Heading size="lg">Create New Session Type</Heading>
          
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Client ID (yours)</FormLabel>
                <Input
                  value={user.id}
                  isReadOnly
                  bg="gray.100"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Format</FormLabel>
                <Select
                  value={formData.format}
                  onChange={(e) => setFormData({ ...formData, format: e.target.value as "INDIVIDUAL" | "GROUP" })}
                >
                  <option value="INDIVIDUAL">Individual</option>
                  <option value="GROUP">Group</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Duration (minutes)</FormLabel>
                <NumberInput
                  value={formData.duration}
                  onChange={(_, value) => setFormData({ ...formData, duration: value })}
                  min={15}
                  step={15}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Price ($)</FormLabel>
                <NumberInput
                  value={formData.price}
                  onChange={(_, value) => setFormData({ ...formData, price: value })}
                  min={0}
                  precision={2}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Color</FormLabel>
                <Input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Meeting Link (optional)</FormLabel>
                <Input
                  placeholder="https://zoom.us/j/example"
                  value={formData.meetingLink}
                  onChange={(e) => setFormData({ ...formData, meetingLink: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <Checkbox
                  isChecked={formData.availableOnCalendar}
                  onChange={(e) => setFormData({ ...formData, availableOnCalendar: e.target.checked })}
                >
                  Available on calendar
                </Checkbox>
              </FormControl>

              <Button 
                type="submit" 
                colorScheme="blue" 
                w="full" 
                isLoading={loading}
              >
                Create Session Type
              </Button>
            </VStack>
          </form>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </>
  );
}; 